import { Link } from "react-router-dom";
import Header from "@/components/site/Header";
import Footer from "@/components/site/Footer";
import ScrollProgress from "@/components/site/ScrollProgress";
import MobileStickyCTA from "@/components/site/MobileStickyCTA";
import HudCorners from "@/components/site/_partials/HudCorners";
import useDocumentMeta from "@/hooks/useDocumentMeta";
import { organizationSchema, OG_IMAGES, SITE_URL} from "@/lib/seo";
import { analytics } from "@/lib/analytics";
import { ACTIVATION_URL } from "@/lib/links";

const FORM_ID = process.env.REACT_APP_JOTFORM_EMBED_ID;

export default function GetStarted() {
  const formId = FORM_ID;
  const trackFormStart = () => {
    analytics.formStarted({ form_name: "optimization_activation", form_id: formId });
  };
  useDocumentMeta({
    title: "Start My Optimization Activation | Integrated Wellness",
    description:
      "Start your Integrated Wellness Optimization Activation. This guided intake captures your goals, current context and available health data for analysis and planning.",
    image: OG_IMAGES.getStarted,
    url: `${SITE_URL}/get-started`,
    schema: [organizationSchema()],
  });

  return (
    <div data-testid="get-started-page" className="min-h-screen bg-background">
      <ScrollProgress />
      <Header />

      <main className="pt-28 md:pt-32 pb-20 md:pb-28">
        {/* Hero */}
        <section className="relative max-w-6xl mx-auto px-4 md:px-8">
          <div className="absolute inset-0 iw-grid-bg opacity-40 -z-10" aria-hidden="true" />
          <Link
            to="/"
            data-testid="back-link"
            className="eyebrow inline-flex items-center gap-2 text-foreground/65 hover:text-accent transition-colors"
          >
            <span aria-hidden>←</span> Back to Integrated Wellness
          </Link>
          <div className="mt-8 iw-overline">Optimization Activation</div>
          <h1 className="mt-4 font-display font-bold tracking-tight leading-[1.0] text-[clamp(2.25rem,5.5vw,4.5rem)] text-primary">
            Start My <span className="text-accent">Optimization Activation</span>.
          </h1>
          <p className="mt-5 text-lg md:text-xl text-foreground/75 leading-relaxed max-w-3xl">
            A guided intake for your goals, current context and available health data.
          </p>
        </section>

        {/* Secure form handoff */}
        <section className="max-w-5xl mx-auto px-4 md:px-8 mt-14">
          <div data-testid="get-started-form-handoff" className="relative border-2 border-primary bg-card p-8 md:p-12 text-center shadow-[0_24px_60px_-30px_rgba(17,41,107,0.35)]">
            <HudCorners offset={12} size={12} />
            <div className="iw-overline absolute -top-3 left-6 bg-background px-2">
              Secure Jotform Intake
            </div>
            <h2 className="font-display text-2xl md:text-4xl font-bold text-primary">Continue to your Optimization Activation.</h2>
            <p className="mt-4 text-base md:text-lg text-foreground/70">The intake opens directly on the official Integrated Wellness Jotform domain.</p>
            <a
              href={ACTIVATION_URL}
              target="_blank"
              rel="noopener noreferrer"
              onClick={trackFormStart}
              data-testid="get-started-form-link"
              className="mt-7 iw-btn-red inline-flex items-center gap-2 px-7 py-4 text-xs"
            >
              Open Optimization Activation <span aria-hidden>→</span>
            </a>
          </div>

          <p className="mt-6 text-center text-sm text-foreground/60 max-w-2xl mx-auto">
            Your responses are routed securely to our care team. Need a hand?{" "}
            <Link to="/contact" className="text-accent hover:underline font-semibold">
              Contact us
            </Link>{" "}
            or{" "}
            <Link to="/agents/rhea" className="text-accent hover:underline font-semibold">
              talk to Rhea
            </Link>{" "}
            — no signup required.
          </p>
        </section>
      </main>

      <Footer />
      <MobileStickyCTA />
    </div>
  );
}
